/* eslint-disable @typescript-eslint/no-explicit-any */
import { ALL_PRODUCT_TYPES, MULTI_PRODUCT_LINE_STYLES } from "constants/chart.constants";
import { MultiProductFeature } from "types/charts/feature";
import { getBoeRatioLabel } from "utils/chart/productNameHelpers";
import { seriesMatchesProduct } from "utils/chart/seriesMatchesProduct";

import { getEffectiveProductLineStyle } from "./getEffectiveProductLineStyle";
import { limitYAxesPerSide } from "./utils";

// The chart's raw dash arrays (MULTI_PRODUCT_LINE_STYLES) are tuned to read at
// this width (the product-selector preview). Legend swatches are narrower, so
// they scale the dash pattern by SWATCH_WIDTH / SWATCH_REFERENCE_WIDTH to render
// proportionally identical — instead of one long dash filling the swatch.
export const SWATCH_REFERENCE_WIDTH = 50;
// Rendered width of a legend line swatch. Shared by the bottom legend
// (mpcProductLegend) and the beside-axis legend (mpcAxisLegend) so both draw the
// dash pattern identically.
export const SWATCH_WIDTH = 38;

const PRODUCT_ENTRIES = Object.values(ALL_PRODUCT_TYPES) as Array<{
  key: string;
  label: string;
}>;

/** Display label for a product that may be referenced by key or by label. */
export function resolveProductLabel(product: string): string {
  const entry =
    PRODUCT_ENTRIES.find((p) => p.label === product) ??
    PRODUCT_ENTRIES.find((p) => p.key === product);
  return entry?.label ?? product;
}

/**
 * Display label for a legend entry: the product's label, with the BOE family's gas
 * ratio put back. The axis title carries the ratio, so a rider or a bottom-legend
 * entry that used the bare label named the same family two ways on one chart.
 * Shared by the beside-axis legend and the bottom legend.
 */
export function resolveProductLegendLabel(product: string): string {
  return getBoeRatioLabel(resolveProductLabel(product));
}

/**
 * Builds a Map from product name → line style (solid, dashed, or dash-array).
 * Uses persisted user selections when available, falling back to auto-assignment
 * cycling through MULTI_PRODUCT_LINE_STYLES. Under "Override Bin Colours" every
 * product collapses to solid unless the user picked a style during the override
 * — see getEffectiveProductLineStyle.
 */
export function buildProductStyleMap(
  multiProduct: Maybe<Partial<MultiProductFeature>>
): Map<string, string | number[]> {
  const primaryProducts = multiProduct?.properties?.primaryProducts || [];
  const secondaryProducts = multiProduct?.properties?.secondaryProducts || [];
  const allProducts = [...new Set([...primaryProducts, ...secondaryProducts])];

  const productStyleMap = new Map<string, string | number[]>();
  allProducts.forEach((prod, index) => {
    productStyleMap.set(
      prod,
      getEffectiveProductLineStyle(
        prod,
        multiProduct,
        MULTI_PRODUCT_LINE_STYLES[index % MULTI_PRODUCT_LINE_STYLES.length]
      )
    );
  });

  return productStyleMap;
}

/**
 * Returns the line style for a series based on product matching.
 * Forecasts use dashed/dotted styles; historical data uses product-based styling.
 */
export function getLineStyleForSeries(
  productStyleMap: Map<string, string | number[]>,
  seriesItem: { label?: string; groupTitle?: string },
  isForecast: boolean,
  isComparisonForecast: boolean
): string | number[] {
  if (isForecast) {
    return isComparisonForecast ? "dotted" : "dashed";
  }

  for (const [product, style] of productStyleMap.entries()) {
    if (seriesMatchesProduct(seriesItem, product)) {
      return style;
    }
  }

  return "solid";
}

/**
 * Infers the product for an axis by matching series labels against the known
 * product list. Falls back to the chart's default product when no match is found.
 */
export function inferAxisProduct(
  allProducts: string[],
  seriesForThisAxis: { label?: string; groupTitle?: string }[],
  fallbackProduct: string
): string {
  return (
    allProducts.find((p) =>
      seriesForThisAxis.some(
        (s) =>
          s.label === p ||
          s.groupTitle === p ||
          seriesMatchesProduct(s, p, { exactAlias: true })
      )
    ) ?? fallbackProduct
  );
}

/**
 * Determines whether an axis should be positioned on the left (primary) or
 * right (secondary) side based on the products its series match.
 */
export function getYAxisPosition(
  primaryProducts: string[],
  secondaryProducts: string[],
  seriesForThisAxis: { label?: string; groupTitle?: string }[]
): "left" | "right" {
  const matchesProduct = (s: { label?: string; groupTitle?: string }, p: string) =>
    s.label === p ||
    s.groupTitle === p ||
    seriesMatchesProduct(s, p, { exactAlias: true });

  const hasAnyPrimary = primaryProducts?.some((p) =>
    seriesForThisAxis.some((s) => matchesProduct(s, p))
  );
  const isSecondaryAxis =
    !hasAnyPrimary &&
    secondaryProducts?.some((p) => seriesForThisAxis.some((s) => matchesProduct(s, p)));
  return isSecondaryAxis ? "right" : "left";
}

/**
 * Filters out Y-axes with no matching series, caps at 2 per side, and builds
 * a remap from original axis id → new array index so series yAxisIndex values
 * stay valid after filtering.
 *
 * Note: Axes dropped by limitYAxesPerSide intentionally have no remap entry.
 * Callers use `remap.get(index) ?? 0` to fall back to the first axis.
 */
export function buildYAxisIndexRemap(
  multiYAxis: any[],
  series: { yAxisIndex?: number }[]
): { filteredAxes: any[]; remap: Map<number, number> } {
  const remap = new Map<number, number>();
  const usedIndices = new Set(series.map((s) => s.yAxisIndex ?? 0));
  const usedAxes = multiYAxis.filter((axis) => usedIndices.has(axis.id as number));
  const cappedAxes = limitYAxesPerSide(usedAxes);
  cappedAxes.forEach((axis, newIndex) => {
    remap.set(axis.id as number, newIndex);
  });
  return { filteredAxes: cappedAxes, remap };
}
